// portfolioData.js
// This file contains all the data for the portfolio website.
// Edit this file to update content without changing the code structure.

export const personalInfo = {
  name: "Arunav Saha",
  title: "Computer Science Student",
  location: "Memorial University of Newfoundland",
  email: "arunavs@mun.ca",
  github: "https://github.com/ARNAB814",
  linkedin: "https://www.linkedin.com/in/arunavsaha",
  about:
    "Computer Science student passionate about technology and innovation. Currently in my 4th year of a Bachelor of Science (Hons.) program.",
  resume: "https://github.com/ARNAB814/Resume/blob/main/Resume.pdf",
};

export const heroData = {
  headline: "Welcome to my website!",
  subheading:
    "Hey there! I'm Arunav Saha, currently in my 4th year of a Bachelor of Science (Hons.) in Computer Science at Memorial University of Newfoundland. Thank you for visiting my portfolio!",
  techStack: [
    "Python",
    "JavaScript",
    "React",
    "Node.js",
    "Java",
    "C++",
    "Astro",
  ],
};

export const workExperienceData = [
  {
    id: 1,
    role: "Autonomous Vehicle Researcher",
    company: "Memorial University",
    location: "St. John's, NL",
    duration: "May 2023 - Dec 2023",
    description:
      "Worked under faculty supervision to improve the efficiency and safety of autonomous vehicles in real-world scenarios.",
    responsibilities: [
      "Used Unreal Engine to conduct simulations and enhance edge computing performance",
      "Focused on emergency vehicle scenarios, lane allocation, and variable speed",
      "Implemented V2V communication protocols for improved safety",
      "Optimized data processing for real-time decision-making",
    ],
    technologies: [
      "Unreal Engine",
      "Python",
      "Edge Computing",
      "Computer Vision",
    ],
    achievements: [
      "Reduced collision risk by 35% in simulation scenarios",
      "Published research findings in university technical report",
      "Presented at departmental symposium",
    ],
    logo: "/assets/images/mun-logo.png",
  },
  {
    id: 2,
    role: "Resident Assistant",
    company: "Cluett Hall",
    location: "St. John's, NL",
    duration: "Sep 2022 - Present",
    description:
      "Supporting student residents in Cluett Hall, creating a positive living environment and managing community activities.",
    responsibilities: [
      "Fostered a welcoming environment and encouraged open communication among residents",
      "Worked within the Rezlife framework, helping new RAs and improving through supervisor feedback",
      "Organized and executed various events and activities to build community",
      "Handled emergencies calmly, deescalated conflicts, and provided support when needed",
    ],
    technologies: [
      "Conflict Resolution",
      "Event Planning",
      "Community Building",
    ],
    achievements: [
      "Received 'RA of the Month' award twice",
      "Successfully organized 15+ community-building events",
      "Achieved 95% resident satisfaction rating",
    ],
    logo: "/assets/images/cluett-logo.png",
  },
  {
    id: 3,
    role: "Coding Instructor",
    company: "Memorial Engineering Outreach Summer Camp",
    location: "St. John's, NL",
    duration: "May 2023 - Aug 2023, May 2024 - Aug 2024",
    description:
      "Teaching coding to students in grades 1-12, providing hands-on experience with programming concepts and technologies.",
    responsibilities: [
      "Provided coding lessons in Python, Java, and Arduino",
      "Conducted workshops on cybersecurity and privacy management",
      "Worked on robotics projects and troubleshooting various devices",
      "Collaborated with counselors to create engaging learning environment",
    ],
    technologies: ["Python", "Java", "Arduino", "Robotics", "Cybersecurity"],
    achievements: [
      "Developed custom curriculum for different age groups",
      "Received 100% positive feedback from students and parents",
      "Created reusable learning materials adopted by the program",
    ],
    logo: "/assets/images/engineering-logo.png",
  },
  {
    id: 4,
    role: "AI Policy Researcher",
    company: "Memorial University Governance Secretariat",
    location: "St. John's, NL",
    duration: "Jan 2024 - Apr 2024",
    description:
      "Researched the impact of generative AI on academic integrity and proposed policy countermeasures.",
    responsibilities: [
      "Conducted literature review on AI detection tools and their effectiveness",
      "Analyzed existing academic integrity policies across institutions",
      "Drafted policy recommendations for handling AI-generated content",
      "Presented findings to university governance committee",
    ],
    technologies: ["AI Ethics", "Policy Analysis", "Academic Integrity"],
    achievements: [
      "Recommendations incorporated into university policy updates",
      "Created comprehensive report on generative AI detection methods",
      "Developed guidelines for faculty to address AI in coursework",
    ],
    logo: "/assets/images/mun-logo.png",
  },
  {
    id: 5,
    role: "Professional Photographer",
    company: "Memorial University",
    location: "St. John's, NL",
    duration: "Part-time",
    description:
      "Engaged in digital marketing, event coverage, portrait, and architectural photography.",
    responsibilities: [
      "Photographed university events and activities",
      "Created content for marketing materials and social media",
      "Edited and processed images for various uses",
      "Collaborated with marketing team for specific needs",
    ],
    technologies: [
      "Photography",
      "Adobe Lightroom",
      "Adobe Photoshop",
      "Digital Marketing",
    ],
    achievements: [
      "Photos featured in university publications",
      "Created visual archives of key university events",
      "Developed unique style for university-branded photography",
    ],
    logo: "/assets/images/mun-logo.png",
  },
  {
    id: 6,
    role: "Community Manager",
    company: "Reflective Teens",
    location: "Remote",
    duration: "Jan 2020 - Feb 2022",
    description:
      "Led an international community, coordinated events, recruited team members, and ensured a safe and inclusive environment.",
    responsibilities: [
      "Managed online community of young writers and creators",
      "Organized virtual events and writing workshops",
      "Recruited and trained volunteer moderators",
      "Developed community guidelines and enforced standards",
    ],
    technologies: [
      "Community Management",
      "Content Moderation",
      "Event Planning",
      "Team Leadership",
    ],
    achievements: [
      "Grew community by 75% during tenure",
      "Successfully organized international virtual writing festival",
      "Launched mentorship program connecting young writers with professionals",
    ],
    logo: "/assets/images/rt-logo.png",
  },
];

export const coursesData = {
  "Computer Science Fundamentals": [
    {
      id: 1,
      title: "Data Structures and Algorithms",
      provider: "Memorial University",
      date: "Fall 2022",
      description:
        "Comprehensive study of data structures and algorithms with implementation in Java",
      learnings: [
        "Implemented advanced data structures (trees, graphs, hash tables)",
        "Analyzed algorithm complexity and optimization techniques",
        "Developed efficient sorting and searching algorithms",
        "Applied problem-solving approaches to computational challenges",
      ],
      technologies: ["Java", "Algorithm Analysis", "Data Structures"],
      projects: [
        "Implemented a pathfinding visualizer",
        "Developed an efficient database indexing system",
      ],
      certificate: false,
    },
    {
      id: 2,
      title: "Operating Systems",
      provider: "Memorial University",
      date: "Winter 2023",
      description:
        "In-depth exploration of operating system principles and implementation",
      learnings: [
        "Process management and scheduling algorithms",
        "Memory management techniques and virtual memory",
        "File systems design and implementation",
        "Concurrency control and synchronization",
      ],
      technologies: ["C", "Linux", "Process Management", "Memory Management"],
      projects: [
        "Developed a simple shell",
        "Implemented a virtual memory manager",
      ],
      certificate: false,
    },
  ],
  "Web Development": [
    {
      id: 3,
      title: "Modern JavaScript",
      provider: "Udemy",
      date: "Summer 2023",
      description:
        "Comprehensive course on modern JavaScript features and application development",
      learnings: [
        "ES6+ features and syntax",
        "Asynchronous JavaScript with Promises and async/await",
        "DOM manipulation and event handling",
        "Modern application architecture patterns",
      ],
      technologies: ["JavaScript", "ES6+", "DOM API", "Async Programming"],
      projects: [
        "Built an interactive weather dashboard",
        "Developed a task management application",
      ],
      certificate: true,
      certificateUrl: "#",
    },
    {
      id: 4,
      title: "React - The Complete Guide",
      provider: "Udemy",
      date: "Fall 2023",
      description:
        "Comprehensive React framework course covering components, state management and hooks",
      learnings: [
        "Component-based architecture",
        "State management with hooks and context",
        "React Router for navigation",
        "Performance optimization techniques",
      ],
      technologies: ["React", "JavaScript", "React Router", "Hooks"],
      projects: [
        "Built a social media dashboard",
        "Developed an e-commerce product page",
      ],
      certificate: true,
      certificateUrl: "#",
    },
  ],
  "Machine Learning": [
    {
      id: 5,
      title: "Machine Learning Fundamentals",
      provider: "Memorial University",
      date: "Winter 2024",
      description:
        "Introduction to machine learning algorithms and applications",
      learnings: [
        "Supervised and unsupervised learning techniques",
        "Neural networks and deep learning fundamentals",
        "Model evaluation and validation methods",
        "Feature engineering and selection",
      ],
      technologies: ["Python", "TensorFlow", "scikit-learn", "Pandas"],
      projects: [
        "Built an email spam classifier",
        "Developed a recommendation system",
      ],
      certificate: false,
    },
    {
      id: 6,
      title: "Deep Learning Specialization",
      provider: "Coursera",
      date: "Spring 2024",
      description:
        "Comprehensive specialization on deep learning techniques and applications",
      learnings: [
        "Neural network architectures and training",
        "Convolutional networks for computer vision",
        "Sequence models for natural language processing",
        "Generative models and applications",
      ],
      technologies: [
        "Python",
        "PyTorch",
        "TensorFlow",
        "Computer Vision",
        "NLP",
      ],
      projects: [
        "Implemented an image recognition system",
        "Built a sentiment analysis model",
      ],
      certificate: true,
      certificateUrl: "#",
    },
  ],
  "Game Development": [
    {
      id: 7,
      title: "Game Design and Development",
      provider: "Memorial University",
      date: "Fall 2023",
      description: "Fundamentals of game design, mechanics, and development",
      learnings: [
        "Game design principles and player psychology",
        "2D and 3D game development techniques",
        "Game physics and collision detection",
        "User interface design for games",
      ],
      technologies: ["C++", "Unity", "SFML", "Game Design"],
      projects: [
        "Developed a 2D platformer game",
        "Created a physics-based puzzle game",
      ],
      certificate: false,
    },
    {
      id: 8,
      title: "Unreal Engine 5: Complete Developer Course",
      provider: "Udemy",
      date: "Spring 2024",
      description:
        "Comprehensive course on game development with Unreal Engine 5",
      learnings: [
        "Blueprints visual scripting system",
        "C++ programming for Unreal Engine",
        "3D modeling and animation integration",
        "Advanced rendering and lighting techniques",
      ],
      technologies: ["Unreal Engine", "C++", "Blueprints", "3D Modeling"],
      projects: [
        "Built a first-person adventure game",
        "Developed a vehicle simulation",
      ],
      certificate: true,
      certificateUrl: "#",
    },
  ],
};

export const projectsData = [
  {
    id: 1,
    title: "Email Spam Classifier",
    description:
      "Machine learning model that identifies spam emails with high accuracy using natural language processing techniques.",
    longDescription:
      "This project implements a machine learning classifier that can accurately identify spam emails. It uses various NLP techniques like TF-IDF vectorization and feature engineering combined with a Random Forest classifier. The model achieves over 97% accuracy on test data.",
    thumbnail: "/assets/images/project-spam.jpg",
    tags: ["Python", "Machine Learning", "NLP", "scikit-learn"],
    featured: true,
    github: "https://github.com/ARNAB814/Spam-Email-Classifier",
    demo: null,
    images: [
      "/assets/images/project-spam-1.jpg",
      "/assets/images/project-spam-2.jpg",
    ],
    challenges: [
      "Handling imbalanced dataset with more non-spam than spam emails",
      "Optimizing feature extraction for better performance",
      "Reducing false positives while maintaining high recall",
    ],
    solutions: [
      "Implemented SMOTE for synthetic minority oversampling",
      "Used n-gram analysis and feature importance ranking",
      "Applied threshold tuning and cross-validation",
    ],
    category: "Machine Learning",
  },
  {
    id: 2,
    title: "Game of Life",
    description:
      "Interactive implementation of Conway's Game of Life cellular automaton with customizable rules and patterns.",
    longDescription:
      "A complete implementation of Conway's Game of Life with an interactive UI built in Java. Users can create custom patterns, adjust simulation speed, and modify the rules of the automaton. The project demonstrates object-oriented design principles and efficient algorithms for cellular automata.",
    thumbnail: "/assets/images/project-gameoflife.jpg",
    tags: ["Java", "Algorithms", "Simulation", "UI"],
    featured: true,
    github: "https://github.com/ARNAB814/Game-of-Life",
    demo: null,
    images: [
      "/assets/images/project-gameoflife-1.jpg",
      "/assets/images/project-gameoflife-2.jpg",
    ],
    challenges: [
      "Optimizing performance for large grid sizes",
      "Implementing wraparound boundary conditions",
      "Creating an intuitive user interface for pattern editing",
    ],
    solutions: [
      "Used efficient neighbor checking algorithms",
      "Implemented sparse matrix representation for large patterns",
      "Designed a user-friendly UI with zoom and pan capabilities",
    ],
    category: "Algorithms",
  },
  {
    id: 3,
    title: "Can't Stop Board Game",
    description:
      "Digital adaptation of the classic 'Can't Stop' board game with AI opponents and online multiplayer.",
    longDescription:
      "A complete digital version of the popular board game 'Can't Stop' built with Java Swing. Features include AI opponents with different difficulty levels, customizable game rules, and a robust turn-based system. The game implements the complete ruleset of the original board game with additional digital enhancements.",
    thumbnail: "/assets/images/project-cantstop.jpg",
    tags: ["Java", "Swing", "Game Development", "AI"],
    featured: true,
    github: "https://github.com/ARNAB814/Cant-Stop",
    demo: null,
    images: [
      "/assets/images/project-cantstop-1.jpg",
      "/assets/images/project-cantstop-2.jpg",
    ],
    challenges: [
      "Developing AI opponents with varying strategies",
      "Implementing the complex board game rules accurately",
      "Creating an intuitive user interface for game state visualization",
    ],
    solutions: [
      "Used minimax algorithm with alpha-beta pruning for AI",
      "Developed comprehensive test suite to verify game rules",
      "Designed a visually appealing and user-friendly board representation",
    ],
    category: "Game Development",
  },
  {
    id: 4,
    title: "Knight's Tour Solver",
    description:
      "Algorithm visualization for solving the Knight's Tour problem on chess boards of various sizes.",
    longDescription:
      "An interactive visualizer for the Knight's Tour problem that finds a sequence of moves for a knight to visit every square on a chess board exactly once. Users can choose different board sizes, starting positions, and watch the algorithm work in real-time with adjustable speeds.",
    thumbnail: "/assets/images/project-knight.jpg",
    tags: ["JavaScript", "Algorithms", "Visualization", "Backtracking"],
    featured: false,
    github: "https://github.com/ARNAB814/Knights-Tour",
    demo: null,
    images: [
      "/assets/images/project-knight-1.jpg",
      "/assets/images/project-knight-2.jpg",
    ],
    challenges: [
      "Implementing efficient backtracking algorithm for large boards",
      "Creating a visually engaging and informative animation",
      "Optimizing the solution path finding with heuristics",
    ],
    solutions: [
      "Implemented Warnsdorff's heuristic for optimized path finding",
      "Created step-by-step animation with adjustable speed",
      "Added visual cues to show possible moves and decision making",
    ],
    category: "Algorithms",
  },
  {
    id: 5,
    title: "Portfolio Website",
    description:
      "Personal portfolio website built with Astro and React, featuring a tech-focused design and interactive components.",
    longDescription:
      "A modern portfolio website built using Astro framework with React components. The site features a tech-focused design theme with interactive elements, responsive layouts, and optimized performance. It showcases projects, work experience, courses, and creative content.",
    thumbnail: "/assets/images/project-portfolio.jpg",
    tags: ["Astro", "React", "TailwindCSS", "Web Development"],
    featured: false,
    github: "https://github.com/ARNAB814/Portfolio",
    demo: "https://arunav.netlify.com",
    images: [
      "/assets/images/project-portfolio-1.jpg",
      "/assets/images/project-portfolio-2.jpg",
    ],
    challenges: [
      "Creating a cohesive design that highlights technical projects",
      "Implementing advanced interactive components",
      "Optimizing performance and accessibility",
    ],
    solutions: [
      "Developed a tech-focused theme with consistent visual elements",
      "Used React for interactive components with Astro static generation",
      "Implemented modern web performance best practices",
    ],
    category: "Web Development",
  },
  {
    id: 6,
    title: "Battleship Engine",
    description:
      "Online multiplayer battleship game with custom game modes and realistic sound effects.",
    longDescription:
      "A complete online battleship game implementation with both single-player and multiplayer modes. Features include customizable ship layouts, various game modes with different rules, and a chat system for multiplayer games. The game also includes realistic sound effects and animations for an immersive experience.",
    thumbnail: "/assets/images/project-battleship.jpg",
    tags: ["JavaScript", "HTML", "CSS", "Game Development"],
    featured: false,
    github: null,
    demo: null,
    images: [
      "/assets/images/project-battleship-1.jpg",
      "/assets/images/project-battleship-2.jpg",
    ],
    challenges: [
      "Implementing efficient game state management",
      "Creating a responsive design that works on all devices",
      "Developing a challenging AI opponent for single-player mode",
    ],
    solutions: [
      "Used a state machine approach for game flow management",
      "Implemented responsive CSS grid layouts",
      "Developed multi-level AI with different strategies",
    ],
    category: "Game Development",
  },
  {
    id: 7,
    title: "Ninja Adventure",
    description:
      "2D top-down action RPG game developed in C++ with the SFML framework.",
    longDescription:
      "A 2D action RPG game featuring a ninja character on an adventure through various environments. The game includes combat mechanics, inventory management, and a quest system. Built using C++ and the SFML framework, it demonstrates object-oriented game design principles.",
    thumbnail: "/assets/images/project-ninja.jpg",
    tags: ["C++", "SFML", "Game Development", "Object-Oriented Design"],
    featured: false,
    github: null,
    demo: null,
    images: [
      "/assets/images/project-ninja-1.jpg",
      "/assets/images/project-ninja-2.jpg",
    ],
    challenges: [
      "Creating a flexible entity-component system",
      "Implementing smooth character movement and combat",
      "Designing engaging levels and enemy AI",
    ],
    solutions: [
      "Developed a modular component-based architecture",
      "Implemented physics-based movement with collision detection",
      "Created a behavior tree system for enemy AI",
    ],
    category: "Game Development",
  },
  {
    id: 8,
    title: "Sudoku Solver",
    description:
      "Sudoku puzzle solver and generator with visualization of solving algorithms.",
    longDescription:
      "A comprehensive Sudoku application that can generate puzzles of varying difficulty, solve any valid Sudoku puzzle, and visualize the solving process step by step. The application includes multiple solving algorithms and allows users to play Sudoku with hints and validation.",
    thumbnail: "/assets/images/project-sudoku.jpg",
    tags: ["Python", "Pandas", "Algorithms", "UI/UX"],
    featured: false,
    github: "https://github.com/ARNAB814/Sudoku",
    demo: null,
    images: [
      "/assets/images/project-sudoku-1.jpg",
      "/assets/images/project-sudoku-2.jpg",
    ],
    challenges: [
      "Implementing efficient backtracking for puzzle solving",
      "Generating puzzles with unique solutions of controllable difficulty",
      "Creating an intuitive visualization of solving steps",
    ],
    solutions: [
      "Used constraint propagation with backtracking",
      "Implemented difficulty rating based on required techniques",
      "Developed step-by-step visual explanation of solving process",
    ],
    category: "Algorithms",
  },
];

export const galleryData = [
  {
    id: 1,
    title: "Sunset",
    description: "Vibrant sunset over the ocean with silhouetted horizon",
    category: "Landscape",
    tags: ["sunset", "nature"],
    imageUrl: "/assets/images/gallery-sunset.jpg",
    aspectRatio: "4/3",
    location: "St. John's, NL",
    date: "August 2023",
    camera: "Sony A7III",
    settings: "f/8, 1/125s, ISO 100",
  },
  {
    id: 2,
    title: "Blue",
    description: "Serene blue sky with scattered clouds",
    category: "Landscape",
    tags: ["sky"],
    imageUrl: "/assets/images/gallery-blue.jpg",
    aspectRatio: "3/4",
    location: "Signal Hill, NL",
    date: "July 2023",
    camera: "Sony A7III",
    settings: "f/11, 1/250s, ISO 100",
  },
  {
    id: 3,
    title: "Rainbow",
    description: "Vibrant rainbow after rainfall over mountain landscape",
    category: "Landscape",
    tags: ["sky"],
    imageUrl: "/assets/images/gallery-rainbow.jpg",
    aspectRatio: "1/1",
    location: "Gros Morne, NL",
    date: "June 2023",
    camera: "Sony A7III",
    settings: "f/9, 1/160s, ISO 200",
  },
  {
    id: 4,
    title: "Beach",
    description: "Sandy beach with gentle waves at sunset",
    category: "Landscape",
    tags: ["rocks"],
    imageUrl: "/assets/images/gallery-beach.jpg",
    aspectRatio: "2/1",
    location: "Middle Cove, NL",
    date: "August 2023",
    camera: "Sony A7III",
    settings: "f/8, 1/125s, ISO 100",
  },
  {
    id: 5,
    title: "Freedom",
    description: "Bird soaring high in the clear blue sky",
    category: "Coverage",
    tags: ["event"],
    imageUrl: "/assets/images/gallery-freedom.jpg",
    aspectRatio: "4/3",
    location: "Memorial University, NL",
    date: "May 2023",
    camera: "Sony A7III",
    settings: "f/5.6, 1/500s, ISO 400",
  },
  {
    id: 6,
    title: "City Lights",
    description: "Urban landscape illuminated at night",
    category: "Urban",
    tags: ["night", "city"],
    imageUrl: "/assets/images/gallery-city.jpg",
    aspectRatio: "4/3",
    location: "Downtown St. John's, NL",
    date: "December 2023",
    camera: "Sony A7III",
    settings: "f/4, 2s, ISO 800",
  },
  {
    id: 7,
    title: "Mountain Peaks",
    description: "Snow-capped mountain peaks against blue sky",
    category: "Landscape",
    tags: ["mountains", "snow"],
    imageUrl: "/assets/images/gallery-mountains.jpg",
    aspectRatio: "3/2",
    location: "Rocky Harbour, NL",
    date: "March 2023",
    camera: "Sony A7III",
    settings: "f/11, 1/250s, ISO 200",
  },
  {
    id: 8,
    title: "Autumn Colors",
    description: "Vibrant fall foliage reflected in still lake",
    category: "Nature",
    tags: ["autumn", "trees"],
    imageUrl: "/assets/images/gallery-autumn.jpg",
    aspectRatio: "2/3",
    location: "Pippy Park, NL",
    date: "October 2023",
    camera: "Sony A7III",
    settings: "f/8, 1/125s, ISO 200",
  },
  {
    id: 9,
    title: "Ocean Waves",
    description: "Powerful ocean waves crashing against rocky shore",
    category: "Seascape",
    tags: ["ocean", "waves"],
    imageUrl: "/assets/images/gallery-waves.jpg",
    aspectRatio: "3/2",
    location: "Cape Spear, NL",
    date: "November 2023",
    camera: "Sony A7III",
    settings: "f/10, 1/250s, ISO 400",
  },
  {
    id: 10,
    title: "Northern Lights",
    description: "Aurora borealis dancing in the night sky",
    category: "Night",
    tags: ["aurora", "night"],
    imageUrl: "/assets/images/gallery-aurora.jpg",
    aspectRatio: "3/2",
    location: "Labrador City, NL",
    date: "January 2024",
    camera: "Sony A7III",
    settings: "f/2.8, 15s, ISO 3200",
  },
  {
    id: 11,
    title: "Abandoned Building",
    description: "Atmospheric abandoned building reclaimed by nature",
    category: "Urban",
    tags: ["abandoned", "building"],
    imageUrl: "/assets/images/gallery-abandoned.jpg",
    aspectRatio: "1/1",
    location: "Bonavista, NL",
    date: "July 2023",
    camera: "Sony A7III",
    settings: "f/8, 1/60s, ISO 400",
  },
  {
    id: 12,
    title: "Misty Morning",
    description: "Ethereal morning mist rising over forest landscape",
    category: "Nature",
    tags: ["mist", "forest"],
    imageUrl: "/assets/images/gallery-mist.jpg",
    aspectRatio: "3/2",
    location: "Terra Nova National Park, NL",
    date: "September 2023",
    camera: "Sony A7III",
    settings: "f/8, 1/125s, ISO 400",
  },
];

// This is a partial update to the portfolioData.js file
// This includes the pokemonTeamsData section with the updated sprite URLs

export const pokemonTeamsData = {
  "Pokémon Legends: Arceus": {
    color: "#9F2B68", // A rich fuchsia color
    generation: "Gen 8",
    releaseYear: 2022,
    region: "Hisui",
    description:
      "My journey through the ancient Hisui region, with a powerful team of Alpha Pokémon and legends.",
    team: [
      {
        id: 1,
        name: "Gyarados (Shiny Alpha)",
        type: ["Water", "Flying"],
        level: 85,
        moves: ["Hydro Pump", "Hurricane", "Ice Beam", "Crunch"],
        ability: "Intimidate",
        role: "Special Sweeper",
        description:
          "A rare shiny Alpha Gyarados with tremendous power. Its red coloration and massive size make it an intimidating presence on the battlefield.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/130.png",
      },
      {
        id: 2,
        name: "Lucario (Alpha)",
        type: ["Fighting", "Steel"],
        level: 83,
        moves: ["Close Combat", "Meteor Mash", "Extreme Speed", "Aura Sphere"],
        ability: "Inner Focus",
        role: "Physical Attacker",
        description:
          "An Alpha Lucario with exceptional fighting capabilities. Its aura-sensing abilities and lightning-fast movements make it a formidable ally.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/448.png",
      },
      {
        id: 3,
        name: "Garchomp",
        type: ["Dragon", "Ground"],
        level: 84,
        moves: ["Earthquake", "Dragon Claw", "Fire Fang", "Stone Edge"],
        ability: "Rough Skin",
        role: "Physical Sweeper",
        description:
          "A swift and powerful dragon that can traverse both land and sky. Its razor-sharp fins and incredible speed make it perfect for breaking through defensive teams.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/445.png",
      },
      {
        id: 4,
        name: "Electivire",
        type: ["Electric"],
        level: 82,
        moves: ["Thunder Punch", "Ice Punch", "Earthquake", "Wild Charge"],
        ability: "Motor Drive",
        role: "Physical Attacker",
        description:
          "A powerhouse electric type with incredible physical strength. Its ability to absorb electric attacks makes it a great counter to other electric types.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/466.png",
      },
      {
        id: 5,
        name: "Dialga",
        type: ["Steel", "Dragon"],
        level: 87,
        moves: ["Roar of Time", "Flash Cannon", "Thunder", "Draco Meteor"],
        ability: "Pressure",
        role: "Special Tank",
        description:
          "The legendary deity of time. Its temporal powers and incredible defenses make it nearly unbeatable in prolonged battles.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/483.png",
      },
      {
        id: 6,
        name: "Gardevoir",
        type: ["Psychic", "Fairy"],
        level: 82,
        moves: ["Moonblast", "Psychic", "Shadow Ball", "Thunderbolt"],
        ability: "Trace",
        role: "Special Attacker",
        description:
          "An elegant and powerful psychic Pokémon that can create small black holes with its mind. Its fairy typing gives it excellent coverage against dragon types.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/282.png",
      },
    ],
  },
  "Pokémon FireRed": {
    color: "#FF4136", // Fire red color
    generation: "Gen 3 Remake",
    releaseYear: 2004,
    region: "Kanto",
    description:
      "My classic team that conquered the Indigo League and defeated the Elite Four in the Kanto region.",
    team: [
      {
        id: 1,
        name: "Charizard",
        type: ["Fire", "Flying"],
        level: 78,
        moves: ["Flamethrower", "Dragon Claw", "Earthquake", "Aerial Ace"],
        ability: "Blaze",
        role: "Mixed Attacker",
        description:
          "My first-ever starter that grew into a fearsome dragon. Its fire burns with such intensity that it can melt boulders.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/006.png",
      },
      {
        id: 2,
        name: "Lapras",
        type: ["Water", "Ice"],
        level: 75,
        moves: ["Surf", "Ice Beam", "Thunderbolt", "Psychic"],
        ability: "Water Absorb",
        role: "Special Tank",
        description:
          "A gentle giant with impressive special attack power and bulk. Its melodious singing can calm even the most enraged opponents.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/131.png",
      },
      {
        id: 3,
        name: "Jolteon",
        type: ["Electric"],
        level: 76,
        moves: ["Thunderbolt", "Shadow Ball", "Signal Beam", "Baton Pass"],
        ability: "Volt Absorb",
        role: "Special Sweeper",
        description:
          "Lightning quick and sharp as a needle, this Eeveelution can discharge electricity from its every pore to paralyze opponents.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/135.png",
      },
      {
        id: 4,
        name: "Snorlax",
        type: ["Normal"],
        level: 77,
        moves: ["Body Slam", "Earthquake", "Shadow Ball", "Rest"],
        ability: "Thick Fat",
        role: "Physical Wall",
        description:
          "The immovable object on my team. Its massive bulk and surprising power make it excellent for absorbing hits and striking back hard.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/143.png",
      },
      {
        id: 5,
        name: "Gengar",
        type: ["Ghost", "Poison"],
        level: 77,
        moves: ["Shadow Ball", "Sludge Bomb", "Thunderbolt", "Hypnosis"],
        ability: "Levitate",
        role: "Special Attacker",
        description:
          "A mischievous ghost with the ability to slip through dimensions. Its grinning face is the last thing many opponents see before falling to its spectral powers.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/094.png",
      },
      {
        id: 6,
        name: "Dragonite",
        type: ["Dragon", "Flying"],
        level: 80,
        moves: ["Outrage", "Earthquake", "Thunder Punch", "Extreme Speed"],
        ability: "Inner Focus",
        role: "Physical Sweeper",
        description:
          "The ace of my team, capable of flying around the globe in just 16 hours. Despite its friendly appearance, its power is legendary among trainers.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/149.png",
      },
    ],
  },
  // This is a partial update to the portfolioData.js file
  // This updates just the Emerald team in the pokemonTeamsData section

  "Pokémon Emerald": {
    color: "#50C878", // Emerald green
    generation: "Gen 3",
    releaseYear: 2005,
    region: "Hoenn",
    description:
      "The team that helped me become the Champion of Hoenn and complete the Battle Frontier challenge.",
    team: [
      {
        id: 1,
        name: "Blaziken",
        type: ["Fire", "Fighting"],
        level: 54,
        moves: ["Blaze Kick", "Sky Uppercut", "Slash", "Flamethrower"],
        ability: "Blaze",
        role: "Physical Sweeper",
        description:
          "My trusted starter with legs that can clear 30-story buildings in a single bound. Its fighting style becomes more intense as the battle heats up.",
        sprite:
          "https://img.pokemondb.net/sprites/ruby-sapphire/normal/blaziken.png",
      },
      {
        id: 2,
        name: "Rayquaza",
        type: ["Dragon", "Flying"],
        level: 74,
        moves: ["Outrage", "Fly", "Extreme Speed", "Hyper Beam"],
        ability: "Air Lock",
        role: "Legendary Sweeper",
        description:
          "The mighty legendary dragon that calmed the battle between Kyogre and Groudon. Its power is unmatched in the Hoenn region, making it the centerpiece of my team.",
        sprite:
          "https://img.pokemondb.net/sprites/ruby-sapphire/normal/rayquaza.png",
      },
      {
        id: 3,
        name: "Skarmory",
        type: ["Steel", "Flying"],
        level: 30,
        moves: ["Steel Wing", "Aerial Ace", "Sand Attack", "Fury Attack"],
        ability: "Keen Eye",
        role: "Physical Wall",
        description:
          "A bird Pokémon with a body of steel. Its sharp wings and defensive capabilities make it an excellent physical tank against many of Hoenn's threats.",
        sprite:
          "https://img.pokemondb.net/sprites/ruby-sapphire/normal/skarmory.png",
      },
      {
        id: 4,
        name: "Breloom",
        type: ["Grass", "Fighting"],
        level: 29,
        moves: ["Mach Punch", "Headbutt", "Leech Seed", "Stun Spore"],
        ability: "Effect Spore",
        role: "Physical Attacker",
        description:
          "Despite its small size, this mushroom Pokémon packs a powerful punch with its stretching arms. Its fighting moves provide great coverage against many types.",
        sprite:
          "https://img.pokemondb.net/sprites/ruby-sapphire/normal/breloom.png",
      },
      {
        id: 5,
        name: "Wailmer",
        type: ["Water"],
        level: 26,
        moves: ["Water Gun", "Rollout", "Astonish", "Water Pulse"],
        ability: "Water Veil",
        role: "Special Attacker",
        description:
          "A bouncy water-type Pokémon that stores water in its body. Though not yet fully evolved, it provides important Water-type coverage for the team.",
        sprite:
          "https://img.pokemondb.net/sprites/ruby-sapphire/normal/wailmer.png",
      },
      {
        id: 6,
        name: "Azumarill",
        type: ["Water"],
        level: 60,
        moves: ["Hydro Pump", "Ice Beam", "Strength", "Surf"],
        ability: "Huge Power",
        role: "Physical Attacker",
        description:
          "This deceptively strong Water Pokémon can knock out opponents with its powerful physical attacks thanks to its Huge Power ability. A vital member for water transportation.",
        sprite:
          "https://img.pokemondb.net/sprites/ruby-sapphire/normal/azumarill.png",
      },
    ],
  },
  "Pokémon Scarlet": {
    color: "#FF3E4D",
    generation: "Gen 9",
    releaseYear: 2022,
    region: "Paldea",
    description:
      "My journey through the Paldea region with a team focusing on versatility and type coverage.",
    team: [
      {
        id: 1,
        name: "Meowscarada",
        type: ["Grass", "Dark"],
        level: 78,
        moves: ["Leaf Blade", "Night Slash", "Play Rough", "U-turn"],
        ability: "Protean",
        role: "Fast Physical Attacker",
        description:
          "My starter that evolved into a speedy assassin, perfect for taking down key threats with its diverse movepool.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/908.png",
      },
      {
        id: 2,
        name: "Hydreigon",
        type: ["Dark", "Dragon"],
        level: 75,
        moves: ["Draco Meteor", "Dark Pulse", "Fire Blast", "Flash Cannon"],
        ability: "Levitate",
        role: "Special Sweeper",
        description:
          "A powerful dragon with excellent coverage and the ability to hit hard with special attacks.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/635.png",
      },
      {
        id: 3,
        name: "Gardevoir",
        type: ["Psychic", "Fairy"],
        level: 77,
        moves: ["Moonblast", "Psychic", "Thunderbolt", "Calm Mind"],
        ability: "Trace",
        role: "Special Wall",
        description:
          "Elegant and powerful, serves as both a special tank and a threat against Dragon and Fighting types.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/282.png",
      },
      {
        id: 4,
        name: "Gyarados",
        type: ["Water", "Flying"],
        level: 76,
        moves: ["Waterfall", "Dragon Dance", "Earthquake", "Crunch"],
        ability: "Intimidate",
        role: "Physical Sweeper",
        description:
          "A setup sweeper who can boost its stats and then tear through opposing teams.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/130.png",
      },
      {
        id: 5,
        name: "Armarouge",
        type: ["Fire", "Psychic"],
        level: 75,
        moves: ["Flamethrower", "Psychic", "Energy Ball", "Aura Sphere"],
        ability: "Flash Fire",
        role: "Special Attacker",
        description:
          "A regional specialist with excellent type coverage and the ability to counter many threats.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/936.png",
      },
      {
        id: 6,
        name: "Garchomp",
        type: ["Dragon", "Ground"],
        level: 78,
        moves: ["Earthquake", "Dragon Claw", "Fire Fang", "Swords Dance"],
        ability: "Rough Skin",
        role: "Physical Attacker",
        description:
          "The team's powerhouse with incredible speed and attack, perfect for breaking through walls.",
        sprite:
          "https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/445.png",
      },
    ],
  },
};

// Type color mappings for Pokémon types
export const typeColors = {
  Normal: "#A8A77A",
  Fire: "#EE8130",
  Water: "#6390F0",
  Electric: "#F7D02C",
  Grass: "#7AC74C",
  Ice: "#96D9D6",
  Fighting: "#C22E28",
  Poison: "#A33EA1",
  Ground: "#E2BF65",
  Flying: "#A98FF3",
  Psychic: "#F95587",
  Bug: "#A6B91A",
  Rock: "#B6A136",
  Ghost: "#735797",
  Dragon: "#6F35FC",
  Dark: "#705746",
  Steel: "#B7B7CE",
  Fairy: "#D685AD",
};

// Site configuration
export const siteConfig = {
  navLinks: [
    { id: "home", label: "Home" },
    { id: "work", label: "Work Experience" },
    { id: "courses", label: "Courses" },
    { id: "projects", label: "Projects" },
    { id: "gallery", label: "Gallery" },
    { id: "pokemon", label: "Pokémon" },
  ],
  defaultTheme: "dark", // 'light', 'dark', or 'system'
  themeColor: "#3B82F6", // Primary brand color
  accentColor: "#8B5CF6", // Secondary accent color
  contactEmail: "arunavs@mun.ca",
};
